/**
 * @fileoverview Hook simplificado para estados de UI de TemplateMessagePreview
 * @module useTemplateMessageUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 2.0.0
 */

import { useMemo } from 'react';

/**
 * Hook simplificado para gestión de estados de UI de plantillas de mensajes.
 */
const useTemplateMessageUI = (selectedTemplate, ctaButtons, className = '') => {
  // Determina si debe renderizar el preview
  const shouldRenderPreview = useMemo(() => {
    return Boolean(selectedTemplate && (selectedTemplate.text || selectedTemplate.cards));
  }, [selectedTemplate]);

  // Determina el modo de preview basado en el tipo de template
  const previewMode = useMemo(() => {
    return selectedTemplate?.templateType === 'carousel' ? 'carousel' : 'normal';
  }, [selectedTemplate?.templateType]);

  // Determina si debe mostrar como carousel
  const shouldShowCarousel = useMemo(() => {
    return previewMode === 'carousel';
  }, [previewMode]);

  // Determina si debe mostrar botones CTA (solo en modo normal)
  const shouldShowCTAButtons = useMemo(() => {
    return (ctaButtons && ctaButtons.length > 0) && previewMode !== 'carousel';
  }, [ctaButtons, previewMode]);

  // Configuración de renderizado
  const renderConfig = useMemo(() => ({
    className,
    showPreview: shouldRenderPreview,
    showCTA: shouldShowCTAButtons,
    showCarousel: shouldShowCarousel,
    mode: previewMode,
    wrapperClass: `tw-wrapper ${className}`.trim()
  }), [
    className,
    shouldRenderPreview,
    shouldShowCTAButtons,
    shouldShowCarousel,
    previewMode
  ]);

  return useMemo(() => ({
    shouldRenderPreview,
    shouldShowCTAButtons,
    shouldShowCarousel,
    previewMode,
    renderConfig
  }), [
    shouldRenderPreview,
    shouldShowCTAButtons,
    shouldShowCarousel,
    previewMode,
    renderConfig
  ]);
};

export default useTemplateMessageUI;
